import React, { useState, useCallback, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  LinearProgress,
  Tooltip,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Menu,
  ButtonGroup,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import LockIcon from "@mui/icons-material/Lock";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { usePreventivo, useReorderRows } from "../../hooks/usePreventivo";
import { preventiviApi, PreventivoRow, Candidate } from "../../services/preventiviApi";
import { pickBestPositiveCandidate } from "../../utils/matchSelection";
import { searcherApi } from "../../services/searcherApi";
import { PreventivoStatusChip, CategoryChip, DisciplineChip } from "../../components/common/StatusChip";
import { CATEGORIES } from "../../services/configApi";
import { RowsTable } from "../../components/cost-estimation/RowsTable";
import { AggregationFooter } from "../../components/cost-estimation/AggregationFooter";
import { CloseConfirmDialog } from "../../components/cost-estimation/CloseConfirmDialog";
import { AddManualRowDialog } from "../../components/cost-estimation/AddManualRowDialog";
import { ImportFileDialog } from "../../components/cost-estimation/ImportFileDialog";
import { useQueryClient } from "react-query";
import toast from "react-hot-toast";
import { axiosInstance } from "../../services/apiClient";
import { formatTimeNowRome } from "../../utils/dateFormat";

const AUTOSAVE_DELAY = 1500;

export const PreventivoWorkspacePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: preventivo, isLoading, error } = usePreventivo(id);
  const reorderMutation = useReorderRows(id!);

  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [addRowOpen, setAddRowOpen] = useState(false);
  // When inserting at a specific position, store the anchor row id here.
  const insertAfterRowId = useRef<string | null>(null);
  const [closingLoading, setClosingLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"saved" | "saving" | "error" | null>(null);
  const [confirmDeleteRowId, setConfirmDeleteRowId] = useState<string | null>(null);
  const [deletingRow, setDeletingRow] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [rematchLoading, setRematchLoading] = useState(false);
  const [rematchMenuAnchor, setRematchMenuAnchor] = useState<null | HTMLElement>(null);
  const [rematchAllConfirmOpen, setRematchAllConfirmOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editCategoria, setEditCategoria] = useState("");
  const [editAnno, setEditAnno] = useState<string>("");
  const [editCantiere, setEditCantiere] = useState("");
  const [editCodiceProgetto, setEditCodiceProgetto] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<"ELE" | "MEC">("ELE");
  const saveTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // Accumulates all pending field changes per row so rapid successive edits
  // (e.g. price edit followed immediately by match change) are flushed together
  // in a single API call rather than the earlier change being silently dropped.
  const pendingUpdates = useRef<Map<string, Partial<PreventivoRow>>>(new Map());

  const readonly = preventivo?.status === "closed";
  const isProcessing = preventivo?.status === "processing";
  const isError = preventivo?.status === "error";

  // Notify the user (toast, top-right) when the pipeline failed — macro message
  // with a support code only, never technical details. Deduped per state so
  // refetches don't spam.
  const errorToastKey = useRef<string | null>(null);
  useEffect(() => {
    if (!preventivo) return;
    const errorRows = (preventivo.rows ?? []).filter((r) => r.match_error);
    if (preventivo.status !== "error" && errorRows.length === 0) return;
    const key = `${preventivo.id}:${preventivo.status}:${errorRows.length}`;
    if (errorToastKey.current === key) return;
    errorToastKey.current = key;
    const code = errorRows[0]?.match_error?.match(/codice (RICERCA-\d+)/)?.[1];
    const codeSuffix = code ? ` (codice ${code})` : "";
    toast.error(
      preventivo.status === "error"
        ? `Elaborazione automatica fallita${codeSuffix}. Riprova; se il problema persiste contatta l'assistenza indicando il codice.`
        : `${errorRows.length} ${errorRows.length === 1 ? "riga non elaborata" : "righe non elaborate"}${codeSuffix}. Assegna i match manualmente o rigenera.`,
      { duration: 9000 }
    );
  }, [preventivo]);

  const handleUpdateRow = useCallback((rowId: string, data: Partial<PreventivoRow>) => {
    // Optimistic local update
    qc.setQueryData(["preventivo", id], (old: any) => {
      if (!old?.rows) return old;
      return {
        ...old,
        rows: old.rows.map((r: PreventivoRow) => r.id === rowId ? { ...r, ...data } : r),
      };
    });

    // Accumulate changes: deep-merge overridden_metadata so a price edit is never
    // lost when a match change arrives before the autosave timer fires.
    const current = pendingUpdates.current.get(rowId) ?? {};
    const merged: Partial<PreventivoRow> = { ...current, ...data };
    if (data.overridden_metadata && current.overridden_metadata) {
      merged.overridden_metadata = { ...current.overridden_metadata, ...data.overridden_metadata };
    }
    pendingUpdates.current.set(rowId, merged);

    const existing = saveTimers.current.get(rowId);
    if (existing) clearTimeout(existing);
    setSaveStatus("saving");

    const timer = setTimeout(async () => {
      const toSave = pendingUpdates.current.get(rowId);
      pendingUpdates.current.delete(rowId);
      try {
        if (toSave) await preventiviApi.updateRow(id!, rowId, toSave);
        setSaveStatus("saved");
      } catch {
        setSaveStatus("error");
        toast.error("Errore nel salvataggio");
      }
      saveTimers.current.delete(rowId);
    }, AUTOSAVE_DELAY);

    saveTimers.current.set(rowId, timer);
  }, [id, qc]);

  const handleReorder = useCallback((orderedIds: string[]) => {
    reorderMutation.mutate(orderedIds);
  }, [reorderMutation]);

  const handleDeleteRow = useCallback((rowId: string) => {
    setConfirmDeleteRowId(rowId);
  }, []);

  const confirmDeleteRow = async () => {
    if (!confirmDeleteRowId) return;
    setDeletingRow(true);
    try {
      await preventiviApi.deleteRow(id!, confirmDeleteRowId);
      qc.invalidateQueries(["preventivo", id]);
      toast.success("Riga eliminata");
      setConfirmDeleteRowId(null);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Errore nell'eliminazione");
    } finally {
      setDeletingRow(false);
    }
  };

  const handleAddSection = async (afterRowId?: string | null) => {
    try {
      await preventiviApi.addRow(id!, {
        query_text: "Nuovo capitolo",
        quantity: 0,
        unit: "",
        row_type: "section",
        disciplina: activeTab,
        ...(afterRowId ? { after_row_id: afterRowId } : {}),
      } as any);
      qc.invalidateQueries(["preventivo", id]);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Errore nell'aggiunta del capitolo");
    }
  };

  const handleAddRow = async (data: any, afterRowId?: string | null) => {
    try {
      const newRow = await preventiviApi.addRow(id!, {
        ...data,
        disciplina: activeTab,
        ...(afterRowId ? { after_row_id: afterRowId } : {}),
      });
      qc.invalidateQueries(["preventivo", id]);
      toast.success("Riga aggiunta");

      // Auto-search candidates for the new row
      try {
        const searchResult = await searcherApi.search({ 
          query: data.query_text, 
          top_k: 10,
          matching_mode: "browse", // Manual row creation by user
        });
        if (searchResult.candidates.length > 0) {
          const bestPositive = pickBestPositiveCandidate(
            searchResult.candidates,
            preventivo?.categoria,
          );
          await preventiviApi.updateRow(id!, newRow.id, {
            candidates: searchResult.candidates,
            selected_item_uid: bestPositive?.uid ?? null,
          });
          qc.invalidateQueries(["preventivo", id]);
        }
      } catch {
        // best effort — don't block if search fails
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Errore nell'aggiunta");
    }
  };

  const handleInsertAfter = useCallback((afterRowId: string, type: "item" | "section") => {
    if (type === "section") {
      handleAddSection(afterRowId);
    } else {
      // Open the add-row dialog pre-armed with the anchor row id
      insertAfterRowId.current = afterRowId;
      setAddRowOpen(true);
    }
  }, [id, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = async () => {
    setClosingLoading(true);
    try {
      // Send positive feedback for machine-chosen confirmed rows (not yet sent,
      // not manually overridden). Manually-picked rows already generated
      // feedback at selection time via MatchPickerCell.
      const rows = preventivo?.rows ?? [];
      const feedbackCalls = rows
        .filter((r) => r.selected_item_uid && !r.feedback_sent)
        .flatMap((r) => {
          const selected = r.candidates?.find((c) => c.uid === r.selected_item_uid);
          // Only machine-confirmed positive matches generate implicit positive
          // feedback. Manual picks already sent feedback at selection time;
          // excluded/unclassified selections must never be reinforced.
          if (!selected || !["codice", "exact", "acceptable"].includes(selected.match_type ?? "")) return [];
          return [
            searcherApi.feedback({
              query: r.query_text,
              item_uid: r.selected_item_uid!,
              signal: 1,
            }).catch(() => {}),
          ];
        });
      await Promise.allSettled(feedbackCalls);

      await preventiviApi.close(id!);
      toast.success("Preventivo chiuso");
      setCloseDialogOpen(false);
      qc.invalidateQueries(["preventivo", id]);
      qc.invalidateQueries("preventivi");
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Errore nella chiusura");
    } finally {
      setClosingLoading(false);
    }
  };

  const handleOpenEdit = () => {
    if (!preventivo) return;
    setEditName(preventivo.name);
    setEditDesc(preventivo.description || "");
    setEditCategoria(preventivo.categoria || "");
    setEditAnno(preventivo.anno ? String(preventivo.anno) : "");
    setEditCantiere(preventivo.nome_cantiere || "");
    setEditCodiceProgetto(preventivo.codice_progetto || "");
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editName.trim()) return;
    setEditSaving(true);
    try {
      await preventiviApi.update(id!, {
        name: editName.trim(),
        description: editDesc.trim() || undefined,
        categoria: editCategoria || undefined,
        anno: editAnno ? parseInt(editAnno) : undefined,
        nome_cantiere: editCantiere.trim() || undefined,
        codice_progetto: editCodiceProgetto.trim() || undefined,
      });
      qc.invalidateQueries(["preventivo", id]);
      qc.invalidateQueries("preventivi");
      toast.success("Preventivo aggiornato");
      setEditOpen(false);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Errore nell'aggiornamento");
    } finally {
      setEditSaving(false);
    }
  };

  const handleRematch = async (mode: "missing" | "all") => {
    setRematchMenuAnchor(null);
    setRematchLoading(true);
    try {
      const res = await preventiviApi.rematch(id!, mode);
      if (res.queued === 0) {
        toast.success(res.message ?? "Nessuna riga da rigenerare");
      } else {
        toast.success(`${res.queued} righe accodate per il ricalcolo dei match`);
        qc.invalidateQueries(["preventivo", id]);
        qc.invalidateQueries("preventivi");
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Errore nel ricalcolo dei match");
    } finally {
      setRematchLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const res = await axiosInstance.get(`/preventivi/${id}/export`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const filename = res.headers["content-disposition"]
        ?.split("filename=")[1]?.replace(/"/g, "") || "preventivo.xlsx";
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore nell'esportazione");
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !preventivo) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">Preventivo non trovato.</Alert>
        <Button onClick={() => navigate("/cost-estimation")} sx={{ mt: 2 }}>← Torna alla lista</Button>
      </Box>
    );
  }

  const allRows = [...(preventivo.rows ?? [])].sort((a, b) => a.position - b.position);
  // Filter rows by active discipline tab (ELE or MEC)
  const rows = allRows.filter((r) => !r.disciplina || r.disciplina === activeTab);
  const hasEle = allRows.some((r) => !r.disciplina || r.disciplina === "ELE");
  const hasMec = allRows.some((r) => r.disciplina === "MEC");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Top bar */}
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexShrink: 0,
          bgcolor: "background.paper",
          flexWrap: "wrap",
        }}
      >
        <Tooltip title="Torna alla lista">
          <IconButton size="small" onClick={() => navigate("/cost-estimation")}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1rem" }} noWrap>
              {preventivo.name}
            </Typography>
            {!readonly && !isProcessing && (
              <Tooltip title="Modifica metadati">
                <IconButton size="small" onClick={handleOpenEdit} sx={{ color: "text.disabled" }}>
                  <EditIcon sx={{ fontSize: "0.9rem" }} />
                </IconButton>
              </Tooltip>
            )}
            <PreventivoStatusChip status={preventivo.status} />
            {preventivo.categoria && <CategoryChip categoria={preventivo.categoria} />}
            {preventivo.anno && (
              <Typography variant="caption" color="text.secondary">{preventivo.anno}</Typography>
            )}
            {preventivo.nome_cantiere && (
              <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 160 }}>
                {preventivo.nome_cantiere}
              </Typography>
            )}
            {preventivo.codice_progetto && (
              <Chip label={preventivo.codice_progetto} size="small" variant="outlined" sx={{ height: 18, fontSize: "0.68rem" }} />
            )}
            {saveStatus === "saving" && <Typography variant="caption" color="text.disabled">Salvataggio...</Typography>}
            {saveStatus === "saved" && (
              <Typography variant="caption" color="text.disabled">
                Salvato {formatTimeNowRome()}
              </Typography>
            )}
            {saveStatus === "error" && <Typography variant="caption" color="error">Errore salvataggio</Typography>}
          </Box>
          {isProcessing && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Elaborazione {preventivo.processed_rows}/{preventivo.total_rows}...
              </Typography>
              <LinearProgress
                variant={preventivo.total_rows > 0 ? "determinate" : "indeterminate"}
                value={preventivo.total_rows > 0 ? (preventivo.processed_rows / preventivo.total_rows) * 100 : undefined}
                sx={{ width: 120, borderRadius: 4, height: 4 }}
              />
            </Box>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          {!readonly && !isProcessing && (
            <>
              <Button
                size="small"
                startIcon={<AddIcon />}
                variant="outlined"
                onClick={() => setAddRowOpen(true)}
                sx={{ borderRadius: "8px" }}
              >
                Riga
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleAddSection()}
                sx={{ borderRadius: "8px", borderStyle: "dashed" }}
              >
                + Capitolo
              </Button>
            </>
          )}
          {!readonly && !isProcessing && (
            <Button
              size="small"
              startIcon={<UploadFileIcon />}
              variant="outlined"
              onClick={() => setImportOpen(true)}
              sx={{ borderRadius: "8px" }}
            >
              Importa file
            </Button>
          )}
          {!readonly && !isProcessing && (
            <>
              <ButtonGroup size="small" variant="outlined" sx={{ borderRadius: "8px" }}>
                <Tooltip title="Rigenera i match per le righe che non ne hanno ancora uno">
                  <Button
                    startIcon={rematchLoading ? <CircularProgress size={14} /> : <AutorenewIcon />}
                    onClick={() => handleRematch("missing")}
                    disabled={rematchLoading}
                    sx={{ borderRadius: "8px 0 0 8px" }}
                  >
                    Rigenera match
                  </Button>
                </Tooltip>
                <Button
                  size="small"
                  sx={{ px: 0.5, borderRadius: "0 8px 8px 0" }}
                  onClick={(e) => setRematchMenuAnchor(e.currentTarget)}
                  disabled={rematchLoading}
                >
                  <ArrowDropDownIcon fontSize="small" />
                </Button>
              </ButtonGroup>
              <Menu
                anchorEl={rematchMenuAnchor}
                open={Boolean(rematchMenuAnchor)}
                onClose={() => setRematchMenuAnchor(null)}
                PaperProps={{ sx: { borderRadius: "10px", minWidth: 240 } }}
              >
                <MenuItem
                  onClick={() => handleRematch("missing")}
                  sx={{ fontSize: "0.85rem", gap: 1 }}
                >
                  <AutorenewIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  Solo righe senza match
                </MenuItem>
                <MenuItem
                  onClick={() => { setRematchMenuAnchor(null); setRematchAllConfirmOpen(true); }}
                  sx={{ fontSize: "0.85rem", gap: 1, color: "warning.dark" }}
                >
                  <AutorenewIcon fontSize="small" />
                  Rigenera tutti i match
                </MenuItem>
              </Menu>
            </>
          )}
          <Button
            size="small"
            startIcon={<FileDownloadIcon />}
            variant="outlined"
            onClick={handleExport}
            sx={{ borderRadius: "8px" }}
          >
            Esporta
          </Button>
          {!readonly && !isProcessing && (
            <Button
              size="small"
              startIcon={<LockIcon />}
              variant="outlined"
              color="error"
              onClick={() => setCloseDialogOpen(true)}
              sx={{ borderRadius: "8px" }}
            >
              Chiudi
            </Button>
          )}
        </Box>
      </Box>

      {/* Pipeline error banner */}
      {isError && (
        <Alert
          severity="error"
          sx={{ borderRadius: 0, flexShrink: 0 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={rematchLoading ? <CircularProgress size={14} /> : <AutorenewIcon />}
              onClick={() => handleRematch("missing")}
              disabled={rematchLoading}
            >
              Riprova
            </Button>
          }
        >
          L'elaborazione automatica dei match è fallita. Riprova oppure assegna i match manualmente
          dalle righe contrassegnate con "Errore match".
        </Alert>
      )}

      {/* Table area */}
      {isProcessing ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, gap: 2 }}>
          <CircularProgress />
          <Typography color="text.secondary">
            Elaborazione in corso... puoi chiudere questa pagina e tornare più tardi.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          {/* ELE / MEC tabs */}
          {(hasEle || hasMec) && (
            <Box sx={{ px: 2, pt: 0.25, borderBottom: "1px solid", borderColor: "divider", flexShrink: 0 }}>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                sx={{ minHeight: 32 }}
              >
                <Tab
                  value="ELE"
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "#1565C0" }}>⚡ Elettrico</Typography>
                      <Chip label={allRows.filter((r) => !r.disciplina || r.disciplina === "ELE").length} size="small" sx={{ height: 16, fontSize: "0.62rem" }} />
                    </Box>
                  }
                  sx={{ minHeight: 32, py: 0 }}
                />
                <Tab
                  value="MEC"
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "#E65100" }}>🔧 Meccanico</Typography>
                      <Chip label={allRows.filter((r) => r.disciplina === "MEC").length} size="small" sx={{ height: 16, fontSize: "0.62rem" }} />
                    </Box>
                  }
                  sx={{ minHeight: 32, py: 0 }}
                />
              </Tabs>
            </Box>
          )}
          {rows.length === 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, gap: 2 }}>
              <Typography color="text.secondary">Nessuna riga per la sezione {activeTab}.</Typography>
              {!readonly && (
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddRowOpen(true)}>
                  Aggiungi riga
                </Button>
              )}
            </Box>
          ) : (
            <>
              <RowsTable
                rows={rows}
                readonly={readonly}
                onReorder={handleReorder}
                onUpdateRow={handleUpdateRow}
                onDeleteRow={handleDeleteRow}
                onInsertAfter={!readonly ? handleInsertAfter : undefined}
                preventivoCategoria={preventivo.categoria}
                preventivoId={id!}
                onRematchStarted={() => qc.invalidateQueries(["preventivo", id])}
              />
              <AggregationFooter rows={rows} />
            </>
          )}
        </Box>
      )}

      {/* Close dialog */}
      <CloseConfirmDialog
        open={closeDialogOpen}
        onClose={() => setCloseDialogOpen(false)}
        onConfirm={handleClose}
        rows={rows}
        loading={closingLoading}
      />

      {/* Rematch-all confirmation dialog */}
      <Dialog
        open={rematchAllConfirmOpen}
        onClose={() => setRematchAllConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "1rem" } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Rigenera tutti i match</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            Questa operazione è <strong>irreversibile</strong>.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Verranno cancellati tutti i candidati e i match esistenti, inclusi quelli scelti
            manualmente. Tutte le righe saranno rimesse in coda per una nuova ricerca automatica.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setRematchAllConfirmOpen(false)}>Annulla</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => { setRematchAllConfirmOpen(false); handleRematch("all"); }}
          >
            Conferma e rigenera
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add row dialog */}
      <AddManualRowDialog
        open={addRowOpen}
        onClose={() => { setAddRowOpen(false); insertAfterRowId.current = null; }}
        onAdd={(data) => {
          const afterId = insertAfterRowId.current;
          insertAfterRowId.current = null;
          return handleAddRow(data, afterId);
        }}
      />

      {/* Delete row confirm dialog */}
      <Dialog
        open={!!confirmDeleteRowId}
        onClose={() => !deletingRow && setConfirmDeleteRowId(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "1rem" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}>
          <DeleteOutlineIcon sx={{ color: "#D32F2F" }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Elimina riga</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Sei sicuro di voler eliminare questa riga? L'operazione è irreversibile.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmDeleteRowId(null)} disabled={deletingRow}>
            Annulla
          </Button>
          <Button onClick={confirmDeleteRow} variant="contained" color="error" disabled={deletingRow}>
            {deletingRow ? <CircularProgress size={20} /> : "Elimina"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import file dialog */}
      <ImportFileDialog
        open={importOpen}
        preventivoId={id!}
        defaultDisciplina={activeTab}
        onClose={() => setImportOpen(false)}
        onImported={() => {
          setImportOpen(false);
          qc.invalidateQueries(["preventivo", id]);
          qc.invalidateQueries("preventivi");
        }}
      />

      {/* Edit metadata dialog */}
      <Dialog
        open={editOpen}
        onClose={() => !editSaving && setEditOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "1rem" } }}
      >
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>Modifica metadati preventivo</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "12px !important" }}>
          <TextField
            label="Nome *"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            fullWidth
            disabled={editSaving}
            autoFocus
            size="small"
          />
          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControl size="small" sx={{ flex: 2 }}>
              <InputLabel>Categoria</InputLabel>
              <Select
                value={editCategoria}
                label="Categoria"
                onChange={(e) => setEditCategoria(e.target.value)}
                disabled={editSaving}
              >
                <MenuItem value=""><em>—</em></MenuItem>
                {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label="Anno"
              value={editAnno}
              onChange={(e) => setEditAnno(e.target.value)}
              size="small"
              type="number"
              sx={{ flex: 1 }}
              disabled={editSaving}
            />
          </Box>
          <TextField
            label="Nome cantiere / progetto"
            value={editCantiere}
            onChange={(e) => setEditCantiere(e.target.value)}
            fullWidth
            size="small"
            disabled={editSaving}
          />
          <TextField
            label="Codice progetto"
            value={editCodiceProgetto}
            onChange={(e) => setEditCodiceProgetto(e.target.value)}
            fullWidth
            size="small"
            disabled={editSaving}
          />
          <TextField
            label="Note (opzionale)"
            value={editDesc}
            onChange={(e) => setEditDesc(e.target.value)}
            fullWidth
            multiline
            rows={2}
            size="small"
            disabled={editSaving}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEditOpen(false)} disabled={editSaving}>Annulla</Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={editSaving || !editName.trim()}>
            {editSaving ? <CircularProgress size={22} /> : "Salva"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
